import { datadogLogs } from '@datadog/browser-logs'

// This is used to store all Datadog page actions which have not yet been sent.
// Once a page action is sent it should be removed from this map
const logItemsMap = new Map();

/**
 * This represents a page action which is being logged to Datadog.  A new page action can be created,
 * timed and sent to Datadog, the payload can take any form and can be queried in Datadog.
 */
class PageActionLogger {
  constructor(map, actionName, actionType, initialPayload) {
    this.map = map;
    this.payload = Object.assign(
      { actionName: actionName, type: actionType, startTime: performance.now() },
      { ...initialPayload }
    );
    map.set(actionName, this);
  }

  addTimestamp(timeStampName) {
    this.payload[timeStampName] = performance.now() - this.startTime;
    return this;
  }

  updatePayload(payload) {
    Object.assign(this.payload, { ...payload });
    return this;
  }

  send() {
    this.payload.duration = performance.now() - this.payload.startTime;
    datadogLogs.logger.info(this.payload.actionName, this.payload);
    this.map.delete(this.payload.actionName);
  }
}

/**
 * This represents one or more page actions which take place within an overarching process.
 * Individual page actions can be added and sent, each being stamped with the overall process
 * 'actionCategory' to allow querying for all items within Datadog.
 */
class PageActionsLogger {
  constructor(map, category) {
    this.map = map;
    this.actionsCategory = category;
    this.startTime = performance.now();
    this.map.set(category, this);
    this.processActionsMap = new Map();
  }

  addItem(actionName, actionType, initialPayload = {}) {
    if (!this.processActionsMap.get(actionName)) {
      this.processActionsMap.set(
        actionName,
        new PageActionLogger(
          this.map,
          actionName,
          actionType,
          Object.assign({ actionCategory: this.actionsCategory }, { ...initialPayload })
        )
      );
    }
    return this;
  }

  getItem(actionName) {
    return this.processActionsMap.get(actionName);
  }

  sendAll() {
    this.processActionsMap.forEach((pageActionLogger) => {
      pageActionLogger.send();
    });
    datadogLogs.logger.info(this.actionsCategory, {
        actionCategory: this.actionsCategory,
        startTime: this.startTime,
        duration: performance.now() - this.startTime,
      });
    // Remove from map as we sent everything
    this.map.delete(this.actionsCategory);
  }
}

const logError = (error, additionalInfo = {}) => {
  datadogLogs.logger.error(error, additionalInfo);
};

const createLogProcess = (category) => {
  let logProcess = logItemsMap.get(category);
  if (!logProcess) {
    logProcess = new PageActionsLogger(logItemsMap, category);
  }
  return logProcess;
};

const createLogAction = (actionName, actionType, initialPayload = {}) => {
  return new PageActionLogger(logItemsMap, actionName, actionType, initialPayload);
};

const getItem = (key) => {
  return logItemsMap.get(key);
};

export default {
  createLogAction,
  createLogProcess,
  getItem,
  logError,
};
